
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DateRangeFilter, DateRangeType } from '@/components/admin/DateRangeFilter';
import { IssuanceChart } from '@/components/admin/certificates/IssuanceChart';
import { CountyDistributionChart } from '@/components/admin/certificates/CountyDistributionChart';
import { CourseTypeChart } from '@/components/admin/certificates/CourseTypeChart';
import { subDays } from 'date-fns';

export function CertificatesAnalyticsContent() {
  const [dateRange, setDateRange] = useState<DateRangeType>('30days');
  
  // Function to convert string date range to actual date objects
  const getDateRangeFromValue = (value: DateRangeType) => {
    const now = new Date();
    let from = new Date();
    
    if (typeof value === 'object') {
      return value;
    }
    
    switch(value) {
      case 'today':
        from = new Date(now.setHours(0, 0, 0, 0));
        break;
      case '7days':
        from = subDays(now, 7);
        break;
      case '30days':
        from = subDays(now, 30);
        break;
      case '90days':
        from = subDays(now, 90);
        break;
    }
    
    return { from, to: new Date() };
  };
  
  const handleDateRangeChange = (value: DateRangeType) => {
    setDateRange(value);
  };
  
  // Convert the string date range to date objects for the chart components
  const dateRangeObj = getDateRangeFromValue(dateRange);
  
  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Certificate Analytics</h1>
        <DateRangeFilter value={dateRange} onChange={handleDateRangeChange} />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Total Certificates</CardTitle>
            <CardDescription>Total certificates issued to date</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold">--</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>This Month</CardTitle>
            <CardDescription>Certificates issued this month</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold">--</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Active Templates</CardTitle>
            <CardDescription>Number of active certificate templates</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold">--</div>
          </CardContent>
        </Card>
      </div>
      
      <Tabs defaultValue="issuance" className="w-full">
        <TabsList className="grid grid-cols-3 w-[500px]">
          <TabsTrigger value="issuance">Issuance Trends</TabsTrigger>
          <TabsTrigger value="county">County Distribution</TabsTrigger>
          <TabsTrigger value="course">Course Type</TabsTrigger>
        </TabsList>
        
        <TabsContent value="issuance" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Certificate Issuance Trends</CardTitle>
              <CardDescription>Number of certificates issued over time</CardDescription>
            </CardHeader>
            <CardContent className="h-[400px]">
              <IssuanceChart dateRange={dateRangeObj} />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="county" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>County Distribution</CardTitle>
              <CardDescription>Distribution of certificates by county</CardDescription>
            </CardHeader>
            <CardContent className="h-[400px]">
              <CountyDistributionChart dateRange={dateRangeObj} />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="course" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Course Type Distribution</CardTitle>
              <CardDescription>Distribution of certificates by course type</CardDescription>
            </CardHeader>
            <CardContent className="h-[400px]">
              <CourseTypeChart dateRange={dateRangeObj} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
